import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { PropTypes } from 'prop-types';
import { reset, change } from 'redux-form';
import NotFoundPage from 'components/Errors/NotFoundPage';
import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';

import {
  customerDataLoad,
  customerAccountLink,
  customerAccountUnlink,
  customerFormUnlink,
  showCustomerIdChangeModal,
  hideCustomerIdChangeModal,
  processCustomerIdChange,
  clearCompletedCustomerIdChange,
  clearCustomerData
} from 'actions';

import LoadingWrapper from 'components/Common/LoadingWrapper';
import AddCustomerAccountForm from 'components/Customer/AddCustomerAccountForm';
import CustomerAccountList from 'components/Customer/CustomerAccountList';
import CustomerFormList from 'components/Customer/CustomerFormList';
import CustomerIdChangeModal from 'components/Modals/CustomerIdChangeModal';
import ConfirmModal from 'components/Modals/ConfirmModal';
import withContainerErrorBoundary from 'components/ErrorBoundaries/ContainerErrorBoundary/withContainerErrorBoundary';

class Customer extends Component {
  static propTypes = {
    permissions: PropTypes.arrayOf(PropTypes.string),
    dispatch: PropTypes.func.isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        id: PropTypes.string.isRequired
      }).isRequired
    }).isRequired,
    isCustomerIdChangeModalShown: PropTypes.bool.isRequired,
    isLoadingData: PropTypes.bool.isRequired,
    storeCustomer: PropTypes.shape(),
    returnedExistingCustomerId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    history: PropTypes.shape(),
    customerDataLoadError: PropTypes.shape({
      code: PropTypes.number,
      message: PropTypes.string
    })
  };

  static defaultProps = {
    permissions: null,
    storeCustomer: {
      name: '',
      accounts: [],
      forms: [],
      address: {}
    },
    returnedExistingCustomerId: '',
    history: null,
    customerDataLoadError: null
  };

  constructor(props) {
    super(props);

    this.state = {
      isDeleteFormModalShown: false,
      isDeletingForm: false,
      deleteFormError: null,
      formId: null,
      isUnlinkAccountModalShown: false,
      isUnlinkingAccount: false,
      unlinkAccountError: null,
      accountId: null
    };
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    dispatch(customerDataLoad(match.params.id));
  }

  componentDidUpdate(prevProps) {
    const { dispatch, returnedExistingCustomerId, history } = this.props;
    if (returnedExistingCustomerId && prevProps.returnedExistingCustomerId !== returnedExistingCustomerId) {
      dispatch(customerDataLoad(returnedExistingCustomerId));
      setTimeout(() => {
        dispatch(clearCompletedCustomerIdChange());
        history.push(`/customer/${ returnedExistingCustomerId }`);
      }, 2000);
    }
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(clearCustomerData());
  }

  getCertifictionStatus() {
    const { storeCustomer } = this.props;
    if (storeCustomer !== null && storeCustomer.certificationStatus !== null) {
      if (storeCustomer.certificationStatus === 0) {
        return 'Uncertified';
      }

      const statuses = [];
      const expiredStatuses = [];
      /* eslint-disable no-bitwise */
      const isFatca = (storeCustomer.certificationStatus & 1) === 1;
      const isChapter61 = (storeCustomer.certificationStatus & 2) === 2;
      const isNra = isFatca && (storeCustomer.certificationStatus & 4) === 4;
      const isCrs = (storeCustomer.certificationStatus & 8) === 8;

      const isFatcaExpired = (storeCustomer.certificationStatus & 0x1000) === 0x1000;
      const isNraExpired = isFatcaExpired || (storeCustomer.certificationStatus & 0x4000) === 0x4000;
      const isCrsExpired = (storeCustomer.certificationStatus & 0x8000) === 0x8000;
      /* eslint-enable no-bitwise */

      if (isNra) { (isNraExpired ? expiredStatuses : statuses).push('NRA'); }
      if (isFatca) { (isFatcaExpired ? expiredStatuses : statuses).push('FATCA'); }
      if (isChapter61) { (isFatcaExpired ? expiredStatuses : statuses).push('Chapter\u00a061'); }
      if (isCrs) { (isCrsExpired ? expiredStatuses : statuses).push('CRS'); }

      const activeStatus = statuses.length > 0 ? `Certified: ${ statuses.reduce((prev, curr) => `${ prev }, ${ curr }`) }` : '';
      const expiredStatus = expiredStatuses.length > 0 ? `Expired: ${ expiredStatuses.reduce((prev, curr) => `${ prev }, ${ curr }`) }` : '';
      return <React.Fragment>{ activeStatus }{ activeStatus && expiredStatus && <br /> }{ expiredStatus }</React.Fragment>;
    }

    return null;
  }

  openUnlinkFormModal = (formId) => {
    this.setState({ isDeleteFormModalShown: true, formId });
  }

  closeUnlinkFormModal = () => {
    this.setState({ isDeleteFormModalShown: false });
  }

  confirmUnlinkForm = () => {
    const { formId } = this.state;
    this.processFormUnlink(formId);
    this.setState({ isDeleteFormModalShown: false });
  }

  openUnlinkAccountModal = (accountId) => {
    this.setState({ isUnlinkAccountModalShown: true, accountId });
  }

  closeUnlinkAccountModal = () => {
    this.setState({ isUnlinkAccountModalShown: false });
  }

  confirmUnlinkAccount = () => {
    const { accountId } = this.state;
    this.processAccountUnlink(accountId);
    this.setState({ isUnlinkAccountModalShown: false });
  }

  processAccountLink(addedAccountData) {
    const { dispatch, storeCustomer } = this.props;
    dispatch(customerAccountLink(storeCustomer.id, addedAccountData));
    dispatch(reset('addCustomerAccountForm'));
    dispatch(change('addCustomerAccountForm', 'accountNumber', null));
  }

  processAccountUnlink(accountId) {
    const { dispatch, storeCustomer } = this.props;
    dispatch(customerAccountUnlink(storeCustomer.id, accountId));
  }

  processFormUnlink(formId) {
    const { dispatch, storeCustomer } = this.props;
    dispatch(customerFormUnlink(storeCustomer.id, formId));
    this.setState({ isDeleteFormModalShown: false });
  }

  openCustomerIdChangeModal() {
    const { dispatch } = this.props;
    dispatch(showCustomerIdChangeModal());
  }

  closeCustomerIdChangeModal() {
    const { dispatch } = this.props;
    dispatch(hideCustomerIdChangeModal());
  }

  processCustomerIdChangeModal(updatedCustomer) {
    const { dispatch } = this.props;
    dispatch(processCustomerIdChange(updatedCustomer));
    dispatch(reset('customerIdChangeForm'));
    dispatch(change('customerIdChangeForm', 'newCustomerId', null));
  }

  render() {
    const { permissions, isLoadingData, storeCustomer, isCustomerIdChangeModalShown, customerDataLoadError } = this.props;
    const {
      isDeleteFormModalShown,
      isDeletingForm,
      deleteFormError,
      isUnlinkAccountModalShown,
      isUnlinkingAccount,
      unlinkAccountError
    } = this.state;
    const regexp = /(?:T-)(.*)(?:-MP)(.*)/;
    return (
      <div className="container customer-panel">
        { customerDataLoadError && (customerDataLoadError.code !== 0
          && <NotFoundPage caption={ customerDataLoadError.message } />
        ) }
        { !customerDataLoadError && (isLoadingData || storeCustomer == null) && <LoadingWrapper loaded={ !isLoadingData && storeCustomer !== null } />}
        { !customerDataLoadError && !isLoadingData && storeCustomer !== null && <div className="panel">
          <div className="panel-header">
            <div className="panel-title">
              Customer: { storeCustomer ? storeCustomer.customerId : '' }{ storeCustomer && storeCustomer.name && storeCustomer.name.length > 0 ? ` - ${ storeCustomer.name }` : '' }
            </div>
            <AccessControl allowedPermissions={ [ActivityPerms.customerPageEdit] }>
              <button type="submit" className="btn btn-primary" onClick={ () => this.openCustomerIdChangeModal() }>
                Edit Customer ID
              </button>
            </AccessControl>
          </div>
          <div className="panel-body">
            <div className="panel-body-caption no-shrink">
              <p>You can review and edit the Customer tax documents and account links on this page.</p>
            </div>
            <div className="uploaded-pdfs-caption no-shrink">
              <p>
                You also have the option to unlink tax form(s) from this customer.
              </p>
            </div>
            <div className="no-shrink">
              <div className="panel-body-caption">
                <h2><strong>Customer Details:</strong></h2>
              </div>
              <div className="columns">
                <div className="col-4 col-xl-5 col-md-6">
                  <div className="columns">
                    <div className="col-5 panel-item-name">
                      <p><strong>Certification Status: </strong></p>
                    </div>
                    <div className="col-7 panel-item-value">
                      <p>{this.getCertifictionStatus()}</p>
                    </div>
                    <div className="col-5 panel-item-name">
                      <p><strong>Name: </strong></p>
                    </div>
                    <div className="col-7 panel-item-value">
                      <p>{storeCustomer.name !== null ? storeCustomer.name : ''}</p>
                    </div>
                    <div className="col-5 panel-item-name">
                      <p><strong>Customer ID:</strong></p>
                    </div>
                    <div className="col-7 panel-item-value">
                      <p>{storeCustomer.customerId !== null ? storeCustomer.customerId : ''}</p>
                    </div>
                    <div className="col-5 panel-item-name">
                      <p><strong>Permanent Address:</strong></p>
                    </div>
                    <div className="col-7 panel-item-value">
                      <span>{storeCustomer.address.line1 !== null ? storeCustomer.address.line1 : ''}</span>
                      <span>{storeCustomer.address.line2 !== null ? storeCustomer.address.line2 : ''}</span>
                      <span>{storeCustomer.address.postalCode !== null ? storeCustomer.address.postalCode : ''}</span>
                      <span>{storeCustomer.address.country !== null ? storeCustomer.address.country : ''}</span>
                    </div>
                  </div>
                </div>
                <div className="col-4 col-xl-5 col-md-6">
                  <div className="columns">
                    <div className="col-5 panel-item-name">
                      <p><strong>Treaty Country: </strong></p>
                    </div>
                    <div className="col-7 panel-item-value">
                      <p>{storeCustomer.treatyCountry !== null ? storeCustomer.treatyCountry : ''}</p>
                    </div>
                    { storeCustomer.lobCategoryCode && storeCustomer.lobCategoryCode !== '' && <React.Fragment>
                      <div className="col-5 panel-item-name">
                        <p><strong>LOB: </strong></p>
                      </div>
                      <div className="col-7 panel-item-value">
                        <p>{storeCustomer.lobCategoryCode}</p>
                      </div>
                    </React.Fragment>}
                    <div className="col-5 panel-item-name">
                      <p><strong>US TIN:</strong></p>
                    </div>
                    <div className="col-7 panel-item-value">
                      <p>{storeCustomer.usTin}</p>
                    </div>
                    <div className="col-5 panel-item-name">
                      <p><strong>FTIN:</strong></p>
                    </div>
                    <div className="col-7 panel-item-value">
                      <p>{storeCustomer.foreignTin}</p>
                    </div>
                    <div className="col-5 panel-item-name">
                      <p><strong>GIIN:</strong></p>
                    </div>
                    <div className="col-7 panel-item-value">
                      <p>{storeCustomer.giin}</p>
                    </div>
                    <div className="col-5 panel-item-name">
                      <p><strong>Part 2 GIIN:</strong></p>
                    </div>
                    <div className="col-7 panel-item-value">
                      <p>{storeCustomer.part2Giin}</p>
                    </div>
                  </div>
                </div>
                <div className="col-4 col-xl-2 col-md-12" />
              </div>
            </div>

            <div className="no-shrink">
              <div className="panel-body-caption">
                <h2><strong>Classifications:</strong></h2>
              </div>
              <div className="columns">
                <div className="col-4 col-xl-5 col-md-6">
                  <div className="columns">
                    <div className="col-5 panel-item-name">
                      <p><strong>Chapter 3 Status: </strong></p>
                    </div>
                    <div className="col-7 panel-item-value">
                      <p>{storeCustomer.chapter3StatusDescription !== null ? storeCustomer.chapter3StatusDescription : 'N/A'}</p>
                    </div>
                    <div className="col-5 panel-item-name">
                      <p><strong>Chapter 4 Status: </strong></p>
                    </div>
                    <div className="col-7 panel-item-value">
                      <p>{storeCustomer.chapter4StatusDescription !== null ? storeCustomer.chapter4StatusDescription : 'N/A'}</p>
                    </div>
                    <div className="col-5 panel-item-name">
                      <p><strong>Updated By: </strong></p>
                    </div>
                    <div className="col-7 panel-item-value">
                      <p>{storeCustomer.recordSourceDescription !== null ? storeCustomer.recordSourceDescription : 'N/A'}</p>
                    </div>
                  </div>
                </div>
                <div className="col-4 col-xl-5 col-md-6">
                  <div className="columns">
                    <div className="col-5 panel-item-name">
                      <p><strong>CRS Status: </strong></p>
                    </div>
                    <div className="col-7 panel-item-value">
                      <p>{storeCustomer.crsClassificationDescription !== null ? storeCustomer.crsClassificationDescription : 'N/A'}</p>
                    </div>
                    <div className="col-5 panel-item-name">
                      <p><strong>Updated By: </strong></p>
                    </div>
                    <div className="col-7 panel-item-value">
                      <p>{storeCustomer.crsRecordSourceDescription !== null ? storeCustomer.crsRecordSourceDescription : 'N/A'}</p>
                    </div>
                  </div>
                </div>
                <div className="col-4 col-xl-2 col-md-12" />
              </div>
            </div>

            <CustomerIdChangeModal
              id={ storeCustomer.id }
              customerId={ storeCustomer.customerId }
              isShown={ isCustomerIdChangeModalShown }
              onConfirmCustomerIdChange={ updatedCustomer => this.processCustomerIdChangeModal(updatedCustomer) }
              onCancel={ () => this.closeCustomerIdChangeModal() }
              isTempUser={ storeCustomer.customerId && regexp.exec(storeCustomer.customerId) !== null ? regexp.exec(storeCustomer.customerId).length > 0 : false }
            />
            <div className="form">
              <div className="panel-body-caption">
                <h2><strong>Forms Linked to Customer: </strong></h2>
              </div>
              <div>
                <CustomerFormList
                  permissions={ permissions }
                  customer={ storeCustomer }
                  onConfirmFormUnlink={ formId => this.openUnlinkFormModal(formId) }
                />
              </div>
              <div className="panel-body-caption">
                <h2><strong>Accounts Linked to Customer: </strong></h2>
              </div>
              <div>
                <CustomerAccountList
                  customer={ storeCustomer }
                  permissions={ permissions }
                  onConfirmAccountUnlink={ accountId => this.openUnlinkAccountModal(accountId) }
                />
              </div>
              <AccessControl allowedPermissions={ [ActivityPerms.customerPageEdit] }>
                <div className="panel-body-caption">
                  <h2><strong>Link an Account: </strong></h2>
                </div>
                <div className="col-8">
                  <AddCustomerAccountForm onConfirmAccountLink={ updateData => this.processAccountLink(updateData) } />
                </div>
              </AccessControl>
            </div>
          </div>
        </div> }
        <ConfirmModal
          key="unlinkForm"
          isShown={ isDeleteFormModalShown }
          isPerformingAction={ isDeletingForm }
          onConfirm={ () => this.confirmUnlinkForm() }
          onCancel={ () => this.closeUnlinkFormModal() }
          error={ deleteFormError }
          title="Unlink"
          message="Are you sure you want to unlink this form?"
        />
        <ConfirmModal
          key="unlinkAccount"
          isShown={ isUnlinkAccountModalShown }
          isPerformingAction={ isUnlinkingAccount }
          onConfirm={ () => this.confirmUnlinkAccount() }
          onCancel={ () => this.closeUnlinkAccountModal() }
          error={ unlinkAccountError }
          title="Unlink"
          message="Are you sure you want to unlink this account?"
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  const {
    auth: {
      permissions
    },
    customer: {
      storeCustomer,
      isLoadingData,
      isCustomerIdChangeModalShown,
      returnedExistingCustomerId,
      customerDataLoadError
    }
  } = state;

  return {
    permissions,
    storeCustomer,
    isLoadingData,
    isCustomerIdChangeModalShown,
    returnedExistingCustomerId,
    customerDataLoadError
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(withContainerErrorBoundary(Customer, 'Customer')));
